// @flow

import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import SettingsUsers from '../components/pages/settings/users';
import * as Selectors from '../selectors';
import * as UsersActions from '../actions/users';
import { fetchTags } from '../actions/tags';
import { useUserProfilePermissions } from '../components/user-profile/use-user-profile-permissions';
import { useRolesCatalogue } from '../components/pages/settings/users/use-roles-catalogue';

import type { Dispatch } from 'redux';

// Whether a status's user list came back carrying its embedded role rows. A
// list that lost the include under the per-include 403 fallback has no roles
// data, so its rows can't offer role editing.
const hasRolesInclude = (state, status): boolean => {
  const includes = Selectors.getEmbeddedIncludes(state, status);
  return !!includes && includes.indexOf('roles') !== -1;
};

function mapStateToProps(state) {
  return {
    user: Selectors.getUserModel(state),
    locations: Selectors.getLocations(state),
    activeUsers: Selectors.getActiveTableUsers(state),
    invitedUsers: Selectors.getUsersByStatus(state, 'invited'),
    loading: {
      normal: Selectors.getUsersLoading(state, 'normal'),
      invited: Selectors.getUsersLoading(state, 'invited'),
      adopted: Selectors.getUsersLoading(state, 'adopted'),
    },
    errors: {
      normal: Selectors.getUsersError(state, 'normal'),
      invited: Selectors.getUsersError(state, 'invited'),
      adopted: Selectors.getUsersError(state, 'adopted'),
    },
    rolesIncludeLoaded: {
      normal: hasRolesInclude(state, 'normal'),
      invited: hasRolesInclude(state, 'invited'),
      adopted: hasRolesInclude(state, 'adopted'),
    },
  };
}

function mapDispatchToProps(dispatch: Dispatch<*>, ownProps: any) {
  return bindActionCreators(
    {
      ...ownProps,
      fetchAllUsers: UsersActions.fetchAllUsersWithIncludes,
      addRole: UsersActions.addRoleToUser,
      removeRole: UsersActions.removeRoleFromUser,
      removeUser: UsersActions.removeUser,
      fetchTags,
    },
    dispatch,
  );
}

const Connected = connect(mapStateToProps, mapDispatchToProps)(SettingsUsers);

// Resolve user-profile permissions exactly once at the container so descendant
// rows (<PreferencesCell>) don't each call useUserProfilePermissions() — that
// would fire N parallel auth-pkg `Promise.all([.evaluate(), …])` resolutions
// on mount. See spec §3.1.3. The roles catalogue is fetched here for the same
// reason, and because it is scoped to this page rather than to Redux.
const SettingsUsersWithPermissions = (props: Object) => {
  const permissions = useUserProfilePermissions();
  const rolesCatalogue = useRolesCatalogue();
  return (
    <Connected
      {...props}
      permissions={permissions}
      rolesCatalogue={rolesCatalogue}
    />
  );
};

export default SettingsUsersWithPermissions;
